import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Sparkles, Calendar, HelpCircle, ExternalLink, ChevronDown, ChevronUp, X, Users, Shield, TrendingUp, Check, ArrowLeft } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useCredits } from '@/hooks/useCredits'
import { useTheme } from '@/hooks/useTheme'
import { useApp } from '@/store/AppContext'

/* ─── Pro tier registry (mirrors backend core/credits.py PRO_TIERS) ─── */
// IMPORTANT: plan_id values must match backend exactly (pro_tier_1 … pro_tier_15)
// Backend resolves these to Stripe Price IDs via env vars — no Stripe IDs here.
export const PRO_TIERS = [
  { planId: 'pro_tier_1',  tier: 1,  priceNok: 200,    credits: 3_000       },
  { planId: 'pro_tier_2',  tier: 2,  priceNok: 500,    credits: 12_000      },
  { planId: 'pro_tier_3',  tier: 3,  priceNok: 1_000,  credits: 28_000      },
  { planId: 'pro_tier_4',  tier: 4,  priceNok: 2_000,  credits: 63_500      },
  { planId: 'pro_tier_5',  tier: 5,  priceNok: 3_500,  credits: 122_000     },
  { planId: 'pro_tier_6',  tier: 6,  priceNok: 5_000,  credits: 188_000     },
  { planId: 'pro_tier_7',  tier: 7,  priceNok: 8_000,  credits: 322_000     },
  { planId: 'pro_tier_8',  tier: 8,  priceNok: 12_000, credits: 512_500     },
  { planId: 'pro_tier_9',  tier: 9,  priceNok: 18_000, credits: 810_500     },
  { planId: 'pro_tier_10', tier: 10, priceNok: 25_000, credits: 1_180_500   },
  { planId: 'pro_tier_11', tier: 11, priceNok: 35_000, credits: 1_726_500   },
  { planId: 'pro_tier_12', tier: 12, priceNok: 50_000, credits: 2_567_000   },
  { planId: 'pro_tier_13', tier: 13, priceNok: 65_000, credits: 3_464_000   },
  { planId: 'pro_tier_14', tier: 14, priceNok: 80_000, credits: 4_414_000   },
  { planId: 'pro_tier_15', tier: 15, priceNok: 98_000, credits: 5_586_000   },
] as const

type ProTier = typeof PRO_TIERS[number]

function getTierByPlanId(planId: string): ProTier | undefined {
  return PRO_TIERS.find(t => t.planId === planId)
}

function formatCredits(n: number): string {
  return n.toLocaleString('no-NO')
}

function formatPrice(nok: number): string {
  return `kr ${nok.toLocaleString('no-NO')}`
}

const billingCopy = {
  no: {
    title: 'Bruk og fakturering',
    tasks: 'Oppgaver',
    websites: 'Nettsider',
    computers: 'Datamaskiner',
    manage: 'Administrer',
    manageSubscription: 'Administrer abonnement',
    viewInvoices: 'Se fakturaer',
    topUp: 'Fyll på kreditter',
    upgrade: 'Oppgrader',
    changeLevel: 'Endre nivå',
    usageRecord: 'Brukshistorikk',
    details: 'Detaljer',
    date: 'Dato',
    creditsChange: 'Kredittendring',
    renewalDate: 'Fornyelsesdato',
    credits: 'Kreditter',
    freeCredits: 'Gratis kreditter',
    monthlyCredits: 'Månedlige kreditter',
    dailyRefresh: 'Daglige oppdateringskreditter',
    refreshInfo: 'Oppdateres til {{count}} kl. 06:00 hver dag',
    invoices: 'Fakturaer',
    viewAllInvoices: 'Se alle fakturaer',
    amount: 'Beløp',
    download: 'Last ned',
    noUsage: 'Ingen bruk registrert ennå.',
    noInvoices: 'Ingen fakturaer ennå.',
    loadingInvoices: 'Laster fakturaer...',
    free: 'Gratis',
    planFree: 'Sine Gratis',
  },
  en: {
    title: 'Usage & Billing',
    tasks: 'Tasks',
    websites: 'Websites',
    computers: 'Computers',
    manage: 'Manage',
    manageSubscription: 'Manage subscription',
    viewInvoices: 'View invoices',
    topUp: 'Top up credits',
    upgrade: 'Upgrade',
    changeLevel: 'Change level',
    usageRecord: 'Usage record',
    details: 'Details',
    date: 'Date',
    creditsChange: 'Credits change',
    renewalDate: 'Renewal date',
    credits: 'Credits',
    freeCredits: 'Free credits',
    monthlyCredits: 'Monthly credits',
    dailyRefresh: 'Daily refresh credits',
    refreshInfo: 'Refreshes to {{count}} at 06:00 every day',
    invoices: 'Invoices',
    viewAllInvoices: 'View all invoices',
    amount: 'Amount',
    download: 'Download',
    noUsage: 'No usage recorded yet.',
    noInvoices: 'No invoices yet.',
    loadingInvoices: 'Loading invoices...',
    free: 'Free',
    planFree: 'Sine Free',
  },
} as const

/* ─── Billing API helpers ─── */
async function createCheckoutSession(planId: string, token: string): Promise<string | null> {
  const { API_BASE } = await import('@/lib/config')
  const { apiFetch } = await import('@/lib/api')
  const res = await apiFetch(`${API_BASE}/create-checkout-session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      plan_id: planId,
      success_url: `${window.location.origin}/?payment=success`,
      cancel_url:  `${window.location.origin}/?payment=canceled`,
    }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error((err as { detail?: string }).detail || `Checkout failed (${res.status})`)
  }
  const data = await res.json() as { url?: string }
  return data.url ?? null
}

interface UpgradeTierResponse {
  status: 'upgraded' | 'downgrade_scheduled'
  from_plan: string
  to_plan: string
  credits_granted: number
  message: string
}

async function upgradeTier(planId: string, token: string): Promise<UpgradeTierResponse> {
  const { API_BASE } = await import('@/lib/config')
  const { apiFetch } = await import('@/lib/api')
  const res = await apiFetch(`${API_BASE}/billing/upgrade-tier`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ plan_id: planId }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error((err as { detail?: string }).detail || `Tier change failed (${res.status})`)
  }
  return res.json() as Promise<UpgradeTierResponse>
}

async function cancelSubscription(token: string): Promise<void> {
  const { API_BASE } = await import('@/lib/config')
  const { apiFetch } = await import('@/lib/api')
  const res = await apiFetch(`${API_BASE}/billing/cancel`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error((err as { detail?: string }).detail || `Cancel failed (${res.status})`)
  }
}

interface StripeInvoice {
  id: string
  date: number
  amount: number
  currency: string
  status: string
  url: string | null
}

async function fetchInvoices(token: string): Promise<StripeInvoice[]> {
  const { API_BASE } = await import('@/lib/config')
  const { apiFetch } = await import('@/lib/api')
  const res = await apiFetch(`${API_BASE}/billing/invoices`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!res.ok) return []
  const data = await res.json() as { invoices?: StripeInvoice[] }
  return data.invoices ?? []
}

function formatInvoiceDate(unixTs: number): string {
  return new Date(unixTs * 1000).toLocaleDateString('nb-NO', {
    day: 'numeric', month: 'short', year: 'numeric',
  })
}

function formatInvoiceAmount(amountCents: number, currency: string): string {
  return new Intl.NumberFormat('nb-NO', {
    style: 'currency', currency: currency.toUpperCase(),
  }).format(amountCents / 100)
}

/* ─── Theme-aware color tokens ─── */
function useColors() {
  const { isDark } = useTheme()
  return {
    bg:               isDark ? '#1A1A1A' : '#FFFFFF',
    bgCard:           isDark ? '#1E1E1E' : '#F8F8F8',
    bgMuted:          isDark ? '#252525' : '#F0F0F0',
    bgInput:          isDark ? '#1E1E1E' : '#FFFFFF',
    bgHighlight:      isDark ? '#252525' : '#EBF5FF',
    bgSelected:       isDark ? '#1C2A1C' : '#F0FFF0',
    bgDropdown:       isDark ? '#1C1C1C' : '#FFFFFF',
    bgDropdownHover:  isDark ? '#252525' : '#F5F5F5',
    bgDropdownActive: isDark ? '#2A2A2A' : '#EEF2FF',
    border:           isDark ? '#2E2E2E' : '#E5E5E5',
    borderMid:        isDark ? '#3A3A3A' : '#D8D8D8',
    borderGreen:      isDark ? '#2A4A2A' : '#86EFAC',
    borderDropdown:   isDark ? '#333333' : '#E0E0E0',
    text:             isDark ? '#E5E5E5' : '#1A1A1A',
    textMuted:        isDark ? '#5A5A5A' : '#6A6A6A',
    textDim:          isDark ? '#9A9A9A' : '#7A7A7A',
    textGreen:        isDark ? '#4ADE80' : '#16A34A',
    btnBg:            isDark ? '#E5E5E5' : '#1A1A1A',
    btnText:          isDark ? '#0A0A0A' : '#FFFFFF',
    accent:           isDark ? '#F0F0F0' : '#1A1A1A',
    accentBlue:       '#3B82F6',
    accentBlueDark:   '#2563EB',
    accentText:       isDark ? '#111' : '#FFFFFF',
    modalOverlay:      isDark ? 'rgba(0,0,0,0.76)' : 'rgba(52,50,45,0.42)',
    tierActive:       isDark ? '#1C2A3A' : '#EFF6FF',
    tierActiveBorder: isDark ? '#2A4A6A' : '#93C5FD',
  }
}

/* ─── Scrollable Tier Dropdown (Manus-style) ─── */
interface TierDropdownProps {
  selectedPlanId: string
  onSelect: (planId: string) => void
  currentPlanId: string | null
  c: ReturnType<typeof useColors>
}

function TierDropdown({ selectedPlanId, onSelect, currentPlanId, c }: TierDropdownProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const selectedTier = getTierByPlanId(selectedPlanId)

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>
      {/* Trigger button — shows selected tier */}
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          width: '100%',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 14px',
          background: c.bgMuted,
          border: `1px solid ${c.borderMid}`,
          borderRadius: open ? '8px 8px 0 0' : 8,
          color: c.text,
          fontSize: 14, fontWeight: 600,
          cursor: 'pointer', fontFamily: 'inherit',
          transition: 'border-radius 0.1s',
        }}
      >
        <span>
          {selectedTier
            ? `${formatCredits(selectedTier.credits)} kreditter / mnd`
            : 'Velg nivå'}
        </span>
        {open
          ? <ChevronUp size={16} style={{ color: c.textDim, flexShrink: 0 }} />
          : <ChevronDown size={16} style={{ color: c.textDim, flexShrink: 0 }} />
        }
      </button>

      {/* Dropdown list — scrollable, max 260px */}
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          background: c.bgDropdown,
          border: `1px solid ${c.borderDropdown}`,
          borderTop: 'none',
          borderRadius: '0 0 8px 8px',
          maxHeight: 260,
          overflowY: 'auto',
          zIndex: 100,
          boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
        }}>
          {PRO_TIERS.map((tier, idx) => {
            const isSelected = tier.planId === selectedPlanId
            const isCurrent = tier.planId === currentPlanId
            return (
              <button
                key={tier.planId}
                onClick={() => { onSelect(tier.planId); setOpen(false) }}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '11px 16px',
                  background: isSelected ? c.bgDropdownActive : 'transparent',
                  border: 'none',
                  borderBottom: idx < PRO_TIERS.length - 1 ? `1px solid ${c.border}` : 'none',
                  color: isSelected ? c.text : c.textDim,
                  fontSize: 14, fontWeight: isSelected ? 700 : 400,
                  cursor: 'pointer', fontFamily: 'inherit',
                  textAlign: 'left',
                  transition: 'background 0.1s',
                }}
                onMouseEnter={e => {
                  if (!isSelected) (e.currentTarget as HTMLButtonElement).style.background = c.bgDropdownHover
                }}
                onMouseLeave={e => {
                  if (!isSelected) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'
                }}
              >
                <span>
                  <strong>{formatCredits(tier.credits)}</strong> kreditter / mnd
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 13, color: c.textMuted }}>
                    {formatPrice(tier.priceNok)}/mnd
                  </span>
                  {isCurrent && (
                    <span style={{
                      fontSize: 9, fontWeight: 700, padding: '2px 6px',
                      background: c.textGreen, color: '#fff',
                      borderRadius: 4, textTransform: 'uppercase', letterSpacing: '0.05em',
                    }}>Aktiv</span>
                  )}
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

/* ─── Tier Selector Modal (Manus-style two-column layout) ─── */
interface TierSelectorModalProps {
  onClose: () => void
  isPro: boolean
  currentPlanId: string | null
  onSuccess: (result: UpgradeTierResponse | null) => void
}

function TierSelectorModal({ onClose, isPro, currentPlanId, onSuccess }: TierSelectorModalProps) {
  const c = useColors()
  const { session } = useAuth()
  const [selectedPlanId, setSelectedPlanId] = useState<string>(currentPlanId ?? 'pro_tier_1')
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const currentTier = currentPlanId ? getTierByPlanId(currentPlanId) : null
  const selectedTier = getTierByPlanId(selectedPlanId)

  const isUpgrade = currentTier && selectedTier
    ? selectedTier.tier > currentTier.tier
    : !isPro

  const prorateCredits = currentTier && selectedTier && selectedTier.tier > currentTier.tier
    ? selectedTier.credits - currentTier.credits
    : 0

  const handleConfirm = async () => {
    if (!selectedPlanId) return
    setProcessing(true)
    setError(null)
    try {
      const token = session?.access_token
      if (!token) throw new Error('Ikke innlogget')

      if (!isPro) {
        const url = await createCheckoutSession(selectedPlanId, token)
        if (url) {
          window.location.href = url
        } else {
          throw new Error('Kunne ikke starte betaling. Prøv igjen.')
        }
      } else {
        const result = await upgradeTier(selectedPlanId, token)
        onSuccess(result)
        onClose()
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      setError(msg)
    } finally {
      setProcessing(false)
    }
  }

  const handleDowngradeToFree = async () => {
    if (!confirm('Er du sikker på at du vil nedgradere til Gratis? Endringen trer i kraft fra neste fakturadato.')) return
    setProcessing(true)
    setError(null)
    try {
      const token = session?.access_token
      if (!token) throw new Error('Ikke innlogget')
      await cancelSubscription(token)
      onSuccess(null)
      onClose()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setProcessing(false)
    }
  }

  // Features list for Pro
  const features = [
    'Daglige oppdateringskreditter',
    `${selectedTier ? formatCredits(selectedTier.credits) : '3 000'} kreditter per måned`,
    'Dybdeforskning for store oppgaver',
    'Profesjonelle nettsider med dataanalyse',
    'Innsiktsfulle presentasjoner',
    'Bred forskning for tung bruk',
    'Tidlig tilgang til beta-funksjoner',
    '20 samtidige oppgaver',
    '20 planlagte oppgaver',
  ]

  const modal = (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: c.modalOverlay, display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
    }} onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{
        background: c.bg, borderRadius: 16,
        width: 'min(860px, 92vw)',
        maxHeight: '90vh', overflowY: 'auto',
        padding: '28px 28px 24px', position: 'relative',
        border: `1px solid ${c.border}`,
        boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
      }}>
        {/* Close */}
        <button onClick={onClose} style={{
          position: 'absolute', top: 16, right: 16,
          background: 'none', border: 'none', color: c.textMuted,
          cursor: 'pointer', padding: 4, borderRadius: 6,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}><X size={18} /></button>

        {/* Title */}
        <h2 style={{ fontSize: 22, fontWeight: 700, color: c.text, marginBottom: 20, textAlign: 'center' }}>
          {isPro ? 'Endre Pro-nivå' : 'Oppgrader til Sine Pro'}
        </h2>

        {/* Two-column layout: selected tier card + current/compare card */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>

          {/* LEFT: Selected tier — highlighted (Manus active plan style) */}
          <div style={{
            background: c.bgCard,
            border: `2px solid ${c.accentBlue}`,
            borderRadius: 12, padding: '20px 20px 16px',
          }}>
            {/* Price */}
            <div style={{ marginBottom: 4 }}>
              <span style={{ fontSize: 28, fontWeight: 800, color: c.text }}>
                {selectedTier ? formatPrice(selectedTier.priceNok) : 'kr 200'}
              </span>
              <span style={{ fontSize: 13, color: c.textMuted }}> / mnd</span>
            </div>
            <div style={{ fontSize: 12, color: c.textMuted, marginBottom: 16 }}>
              Tilpassbart månedlig bruk
            </div>

            {/* CTA */}
            <button
              onClick={handleConfirm}
              disabled={processing || selectedPlanId === currentPlanId}
              style={{
                width: '100%', padding: '11px 16px', borderRadius: 24,
                background: (processing || selectedPlanId === currentPlanId) ? c.bgMuted : c.accentBlue,
                border: 'none',
                color: (processing || selectedPlanId === currentPlanId) ? c.textMuted : '#fff',
                fontSize: 14, fontWeight: 700,
                cursor: (processing || selectedPlanId === currentPlanId) ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit', marginBottom: 12,
                transition: 'background 0.15s',
              }}
            >
              {processing
                ? 'Behandler...'
                : selectedPlanId === currentPlanId
                  ? 'Allerede på dette nivået'
                  : !isPro
                    ? 'Oppgrader'
                    : isUpgrade
                      ? 'Oppgrader'
                      : 'Nedgrader'
              }
            </button>

            {/* Tier dropdown */}
            <TierDropdown
              selectedPlanId={selectedPlanId}
              onSelect={setSelectedPlanId}
              currentPlanId={currentPlanId}
              c={c}
            />

            {/* Prorate info */}
            {isPro && isUpgrade && prorateCredits > 0 && (
              <div style={{
                marginTop: 10, padding: '8px 12px',
                background: c.bgSelected, border: `1px solid ${c.borderGreen}`,
                borderRadius: 8, fontSize: 12, color: c.textGreen, lineHeight: 1.5,
              }}>
                Du mottar <strong>+{formatCredits(prorateCredits)} kreditter</strong> nå.
                Neste faktura: <strong>{selectedTier ? formatPrice(selectedTier.priceNok) : ''}/mnd</strong>.
              </div>
            )}
            {isPro && !isUpgrade && selectedPlanId !== currentPlanId && (
              <div style={{
                marginTop: 10, padding: '8px 12px',
                background: c.bgMuted, border: `1px solid ${c.borderMid}`,
                borderRadius: 8, fontSize: 12, color: c.textMuted, lineHeight: 1.5,
              }}>
                Nedgradering trer i kraft fra neste fakturadato.
              </div>
            )}

            {/* Feature list */}
            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {features.map((f, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{
                    width: 16, height: 16, borderRadius: '50%',
                    background: 'transparent', border: `1.5px solid ${c.textMuted}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <Check size={9} style={{ color: c.textMuted }} />
                  </div>
                  <span style={{ fontSize: 13, color: c.textDim }}>{f}</span>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: Current plan / compare */}
          <div style={{
            background: c.bgCard,
            border: `1px solid ${c.border}`,
            borderRadius: 12, padding: '20px 20px 16px',
          }}>
            {/* Price */}
            <div style={{ marginBottom: 4 }}>
              <span style={{ fontSize: 28, fontWeight: 800, color: c.text }}>
                {currentTier ? formatPrice(currentTier.priceNok) : 'kr 0'}
              </span>
              <span style={{ fontSize: 13, color: c.textMuted }}> / mnd</span>
            </div>
            <div style={{ fontSize: 12, color: c.textMuted, marginBottom: 16 }}>
              {isPro ? 'Ditt nåværende nivå' : 'Utvidet bruk for produktivitet'}
            </div>

            {/* Downgrade / current plan button */}
            {isPro ? (
              <button
                onClick={handleDowngradeToFree}
                disabled={processing}
                style={{
                  width: '100%', padding: '11px 16px', borderRadius: 24,
                  background: 'transparent',
                  border: `1.5px solid ${c.borderMid}`,
                  color: c.text,
                  fontSize: 14, fontWeight: 600,
                  cursor: processing ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit', marginBottom: 12,
                }}
              >
                Nedgrader til Gratis
              </button>
            ) : (
              <button
                disabled
                style={{
                  width: '100%', padding: '11px 16px', borderRadius: 24,
                  background: 'transparent',
                  border: `1.5px solid ${c.borderMid}`,
                  color: c.textMuted,
                  fontSize: 14, fontWeight: 600,
                  cursor: 'default',
                  fontFamily: 'inherit', marginBottom: 12,
                }}
              >
                Gjeldende plan
              </button>
            )}

            {/* Current credits display (static, no dropdown) */}
            <div style={{
              padding: '10px 14px',
              background: c.bgMuted,
              border: `1px solid ${c.border}`,
              borderRadius: 8, marginBottom: 12,
              fontSize: 14, fontWeight: 600, color: c.textDim,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <span>
                {currentTier
                  ? `${formatCredits(currentTier.credits)} kreditter / mnd`
                  : '100 kreditter / mnd'}
              </span>
            </div>

            {/* Feature list for current plan */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {features.slice(0, isPro ? 9 : 6).map((f, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{
                    width: 16, height: 16, borderRadius: '50%',
                    background: 'transparent', border: `1.5px solid ${c.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <Check size={9} style={{ color: c.border }} />
                  </div>
                  <span style={{ fontSize: 13, color: c.textMuted }}>{f}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            padding: '10px 14px', background: '#2A1A1A',
            border: '1px solid #5A2A2A', borderRadius: 8,
            color: '#F87171', fontSize: 12, marginBottom: 14,
          }}>
            {error}
          </div>
        )}

        {/* Info rows */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
          {[
            {
              icon: <TrendingUp size={16} style={{ color: c.textDim }} />,
              title: 'Skalerer med deg',
              desc: '15 nivåer fra kr 200 til kr 98 000/mnd — høyere nivå gir bedre pris per kreditt.',
              action: null,
            },
            {
              icon: <Users size={16} style={{ color: c.textDim }} />,
              title: 'Sine-planer for team og bedrifter',
              desc: 'Fleksible planer for alle størrelser — fra startups til enterprise.',
              action: { label: 'Kontakt oss', href: '/kontakt' },
            },
            {
              icon: <Shield size={16} style={{ color: c.textDim }} />,
              title: 'Sikkerhet og samsvar',
              desc: 'Sikkerhet på enterprise-nivå og bransjestandardsertifiseringer.',
              action: { label: 'Les mer', href: '/sikkerhet' },
            },
          ].map(row => (
            <div key={row.title} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '12px 14px', background: c.bgMuted,
              border: `1px solid ${c.border}`, borderRadius: 8,
            }}>
              <div style={{
                width: 34, height: 34, background: c.bgCard, borderRadius: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                {row.icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: c.text }}>{row.title}</div>
                <div style={{ fontSize: 12, color: c.textMuted, marginTop: 1 }}>{row.desc}</div>
              </div>
              {row.action && (
                <a href={row.action.href} style={{
                  padding: '6px 14px',
                  background: 'transparent',
                  border: `1px solid ${c.borderMid}`,
                  borderRadius: 6,
                  color: c.text, fontSize: 12, fontWeight: 600,
                  textDecoration: 'none', whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}>
                  {row.action.label}
                </a>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          paddingTop: 14, borderTop: `1px solid ${c.border}`,
        }}>
          <span style={{ fontSize: 12, color: c.textMuted }}>
            Har du spørsmål?{' '}
            <a href="/hjelp" style={{ color: c.accent, textDecoration: 'none' }}>Gå til hjelpesenter</a>.
          </span>
          <div style={{ display: 'flex', gap: 16 }}>
            {isPro && (
              <button onClick={handleDowngradeToFree} style={{
                background: 'none', border: 'none',
                color: c.textMuted, fontSize: 12, cursor: 'pointer',
                fontFamily: 'inherit', textDecoration: 'underline',
              }}>
                Nedgrader til Gratis
              </button>
            )}
            <a href="#" style={{
              fontSize: 12, color: c.textMuted, textDecoration: 'none',
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
              Endre fakturering <ExternalLink size={11} />
            </a>
          </div>
        </div>
      </div>
    </div>
  )

  if (typeof document === 'undefined') return modal
  return createPortal(modal, document.body)
}

/* ─── Main BillingContent component ─── */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function LegacyBillingContent() {
  const c = useColors()
  const { user, session } = useAuth()
  const { profile } = useCredits(user?.id ?? null)
  const isPro = profile?.plan === 'pro'

  const currentPlanId: string | null = isPro
    ? ((profile as { pro_tier_id?: string })?.pro_tier_id ?? 'pro_tier_1')
    : null

  const currentTier = currentPlanId ? getTierByPlanId(currentPlanId) : null
  const MONTHLY_LIMIT = currentTier ? currentTier.credits : 100
  const DAILY_REFRESH = 50

  const [showTierModal, setShowTierModal] = useState(false)
  const [tierChangeResult, setTierChangeResult] = useState<UpgradeTierResponse | null>(null)
  const [invoices, setInvoices] = useState<StripeInvoice[]>([])
  const [invoicesLoading, setInvoicesLoading] = useState(false)

  const availableCredits = profile?.credits ?? 0
  const usedCredits = Math.max(0, MONTHLY_LIMIT - availableCredits)

  const renewalDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('nb-NO', {
    day: 'numeric', month: 'short', year: 'numeric',
  })

  useEffect(() => {
    const token = session?.access_token
    if (!token) return
    setInvoicesLoading(true)
    fetchInvoices(token)
      .then(setInvoices)
      .catch(() => setInvoices([]))
      .finally(() => setInvoicesLoading(false))
  }, [session?.access_token])

  const handleTierSuccess = (result: UpgradeTierResponse | null) => {
    setTierChangeResult(result)
    setTimeout(() => setTierChangeResult(null), 6000)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {/* Title */}
      <div style={{ marginBottom: 6 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: c.text, margin: 0 }}>Fakturering</h2>
        <p style={{ fontSize: 13, color: c.textMuted, margin: '4px 0 0' }}>Administrer abonnementet og kredittene dine</p>
      </div>

      {/* Tier change success banner */}
      {tierChangeResult && (
        <div style={{
          marginTop: 12, padding: '12px 16px',
          background: c.bgSelected, border: `1px solid ${c.borderGreen}`,
          borderRadius: 10, display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <Check size={16} style={{ color: c.textGreen, flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: c.textGreen }}>
              {tierChangeResult.status === 'upgraded' ? 'Oppgradering vellykket!' : 'Nedgradering planlagt'}
            </div>
            <div style={{ fontSize: 12, color: c.textDim, marginTop: 2 }}>
              {tierChangeResult.message}
            </div>
          </div>
          <button onClick={() => setTierChangeResult(null)} style={{
            marginLeft: 'auto', background: 'none', border: 'none',
            color: c.textMuted, cursor: 'pointer', padding: 2,
          }}><X size={14} /></button>
        </div>
      )}

      {/* ── Subscription card ── */}
      <div style={{
        background: c.bgCard, border: `1px solid ${c.border}`,
        borderRadius: 12, padding: '18px 20px', marginTop: 16, marginBottom: 20,
      }}>
        {/* Plan header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: c.text }}>
              {isPro
                ? `Sine Pro${currentTier ? ` — Tier ${currentTier.tier}` : ''}`
                : 'Sine Gratis'}
            </div>
            {isPro && currentTier && (
              <div style={{ fontSize: 12, color: c.textMuted, marginTop: 2 }}>
                {formatPrice(currentTier.priceNok)}/mnd · Fornyelsesdato{' '}
                <span style={{ color: c.textDim, fontWeight: 500 }}>{renewalDate}</span>
              </div>
            )}
            {!isPro && (
              <div style={{ fontSize: 12, color: c.textMuted, marginTop: 2 }}>
                100 kreditter/mnd · Gratis for alltid
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setShowTierModal(true)} style={{
              padding: '7px 16px',
              background: isPro ? c.bgMuted : c.accentBlue,
              border: isPro ? `1px solid ${c.borderMid}` : 'none',
              borderRadius: 8,
              color: isPro ? c.text : '#fff',
              fontSize: 13, fontWeight: isPro ? 500 : 600,
              cursor: 'pointer', fontFamily: 'inherit',
            }}>
              {isPro ? 'Endre nivå' : 'Oppgrader til Pro'}
            </button>
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: c.border, marginBottom: 14 }} />

        {/* Credits row */}
        <div style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Sparkles size={14} style={{ color: c.textDim }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: c.text }}>Kreditter</span>
              <HelpCircle size={12} style={{ color: c.textMuted, cursor: 'help' }} />
            </div>
            <span style={{ fontSize: 15, fontWeight: 700, color: c.text }}>
              {availableCredits.toLocaleString('no-NO')}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <span style={{ fontSize: 12, color: c.textMuted, paddingLeft: 20 }}>Brukt denne måneden</span>
            <span style={{ fontSize: 12, color: c.textMuted }}>{usedCredits.toLocaleString('no-NO')}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: c.textMuted, paddingLeft: 20 }}>Månedlige kreditter</span>
            <span style={{ fontSize: 12, color: c.textMuted }}>
              {availableCredits.toLocaleString('no-NO')} / {MONTHLY_LIMIT.toLocaleString('no-NO')}
            </span>
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: c.border, marginBottom: 12 }} />

        {/* Daily refresh credits */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Calendar size={14} style={{ color: c.textDim }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: c.text }}>Daglige oppdateringskreditter</span>
            <HelpCircle size={12} style={{ color: c.textMuted, cursor: 'help' }} />
          </div>
          <span style={{ fontSize: 15, fontWeight: 700, color: c.text }}>0</span>
        </div>
        <div style={{ fontSize: 12, color: c.textMuted, marginTop: 2, paddingLeft: 20 }}>
          Oppdateres til {DAILY_REFRESH} kl. 06:00 hver dag
        </div>

        {/* Tier info strip for Pro users */}
        {isPro && currentTier && (
          <div style={{
            marginTop: 14, padding: '10px 14px',
            background: c.bgMuted, borderRadius: 8, border: `1px solid ${c.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div style={{ fontSize: 12, color: c.textMuted }}>
              <span style={{ color: c.text, fontWeight: 600 }}>Pro Tier {currentTier.tier}</span>
              {' '}· {formatCredits(currentTier.credits)} kreditter/mnd · {formatPrice(currentTier.priceNok)}/mnd
            </div>
            <button
              onClick={() => setShowTierModal(true)}
              style={{
                background: 'none', border: 'none',
                color: c.accentBlue, fontSize: 12, fontWeight: 500,
                cursor: 'pointer', fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', gap: 4,
              }}
            >
              Endre <ChevronDown size={12} />
            </button>
          </div>
        )}
      </div>

      {/* ── Recent activity ── */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: c.text }}>Nylig aktivitet</span>
          <a href="#" style={{
            fontSize: 12, color: c.textMuted, textDecoration: 'none',
            display: 'flex', alignItems: 'center', gap: 4,
          }}
          onMouseEnter={e => (e.currentTarget.style.color = c.text)}
          onMouseLeave={e => (e.currentTarget.style.color = c.textMuted)}
          >
            Se alle fakturaer <ExternalLink size={11} />
          </a>
        </div>

        {/* Table header */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr auto',
          padding: '6px 0', borderBottom: `1px solid ${c.border}`, marginBottom: 4,
        }}>
          {['Dato', 'Beløp', ''].map(h => (
            <span key={h} style={{ fontSize: 11, color: c.textMuted, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</span>
          ))}
        </div>

        {/* Invoice rows */}
        {invoicesLoading && (
          <div style={{ fontSize: 13, color: c.textMuted, padding: '12px 0' }}>Laster fakturaer...</div>
        )}
        {!invoicesLoading && invoices.length === 0 && (
          <div style={{ fontSize: 13, color: c.textMuted, padding: '12px 0' }}>Ingen fakturaer ennå.</div>
        )}
        {!invoicesLoading && invoices.map((inv) => (
          <div key={inv.id} style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr auto',
            padding: '12px 0', borderBottom: `1px solid ${c.border}`, alignItems: 'center',
          }}>
            <span style={{ fontSize: 13, color: c.textDim }}>{formatInvoiceDate(inv.date)}</span>
            <span style={{ fontSize: 13, color: c.textDim }}>{formatInvoiceAmount(inv.amount, inv.currency)}</span>
            {inv.url ? (
              <a href={inv.url} target="_blank" rel="noreferrer" style={{
                fontSize: 13, color: c.text, textDecoration: 'underline', fontWeight: 500, cursor: 'pointer',
              }}>
                Last ned
              </a>
            ) : (
              <span style={{ fontSize: 13, color: c.textMuted }}>{inv.status}</span>
            )}
          </div>
        ))}
      </div>

      {/* Tier selector modal */}
      {showTierModal && (
        <TierSelectorModal
          onClose={() => setShowTierModal(false)}
          isPro={isPro}
          currentPlanId={currentPlanId}
          onSuccess={handleTierSuccess}
        />
      )}
    </div>
  )
}

export function BillingContent() {
  const { user, session } = useAuth()
  const { settings, conversations } = useApp()
  const { profile, transactions } = useCredits(user?.id ?? null)
  const lang = settings.language === 'en' ? 'en' : 'no'
  const copy = billingCopy[lang]
  const locale = lang === 'no' ? 'nb-NO' : 'en-US'
  const isPro = profile?.plan === 'pro'
  const currentPlanId: string | null = isPro
    ? ((profile as { pro_tier_id?: string })?.pro_tier_id ?? 'pro_tier_1')
    : null
  const currentTier = currentPlanId ? getTierByPlanId(currentPlanId) : null
  const monthlyLimit = currentTier ? currentTier.credits : 100
  const dailyRefresh = 50
  const availableCredits = profile?.credits ?? 0

  const [showTierModal, setShowTierModal] = useState(false)
  const [tierChangeResult, setTierChangeResult] = useState<UpgradeTierResponse | null>(null)
  const [invoices, setInvoices] = useState<StripeInvoice[]>([])
  const [invoicesLoading, setInvoicesLoading] = useState(false)
  const [manageOpen, setManageOpen] = useState(false)
  const [view, setView] = useState<'usage' | 'invoices'>('usage')

  const renewalDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })

  useEffect(() => {
    const token = session?.access_token
    if (!token) return
    setInvoicesLoading(true)
    fetchInvoices(token)
      .then(setInvoices)
      .catch(() => setInvoices([]))
      .finally(() => setInvoicesLoading(false))
  }, [session?.access_token])

  const handleTierSuccess = (result: UpgradeTierResponse | null) => {
    setTierChangeResult(result)
    setTimeout(() => setTierChangeResult(null), 6000)
  }

  const usageRows = conversations
    .filter(conv => (conv.creditsUsed ?? 0) > 0)
    .map(conv => ({
      id: `conv-${conv.id}`,
      details: conv.title || (lang === 'no' ? 'Samtale' : 'Conversation'),
      date: conv.updatedAt,
      amount: -Math.abs(conv.creditsUsed ?? 0),
    }))
    .concat(
      transactions
        .filter(item => !item.type.startsWith('usage_'))
        .map(item => ({
          id: `tx-${item.id}`,
          details: item.description || item.type,
          date: new Date(item.created_at),
          amount: item.amount,
        }))
    )
    .sort((a, b) => b.date.getTime() - a.date.getTime())
    .slice(0, 30)

  const planTitle = isPro ? 'Sine Pro' : copy.planFree
  const planMeta = isPro
    ? `${copy.renewalDate} ${renewalDate}`
    : `100 ${copy.monthlyCredits.toLowerCase()} · ${copy.free}`

  const openInvoices = () => {
    setManageOpen(false)
    setView('invoices')
  }

  if (view === 'invoices') {
    return (
      <div className="usage-billing-page">
        <div className="usage-billing-invoices-head">
          <button className="usage-billing-back" onClick={() => setView('usage')}>
            <ArrowLeft size={18} />
            <span>{copy.invoices}</span>
          </button>
          <button className="usage-billing-secondary-btn" onClick={openInvoices}>
            {copy.viewAllInvoices} <ExternalLink size={12} />
          </button>
        </div>

        <div className="usage-billing-table usage-billing-table--invoices">
          <div className="usage-billing-table-head">
            <span>{copy.date}</span>
            <span>{copy.amount}</span>
            <span />
          </div>
          {invoicesLoading && <div className="usage-billing-empty">{copy.loadingInvoices}</div>}
          {!invoicesLoading && invoices.length === 0 && <div className="usage-billing-empty">{copy.noInvoices}</div>}
          {!invoicesLoading && invoices.map(inv => (
            <div key={inv.id} className="usage-billing-table-row">
              <span>{formatInvoiceDate(inv.date)}</span>
              <span>{formatInvoiceAmount(inv.amount, inv.currency)}</span>
              {inv.url ? (
                <a href={inv.url} target="_blank" rel="noreferrer">{copy.download}</a>
              ) : (
                <span className="usage-billing-muted">{inv.status}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="usage-billing-page">
      <div className="usage-billing-title-row">
        <h2>{copy.title}</h2>
      </div>

      <div className="usage-billing-tabs" role="tablist" aria-label={copy.title}>
        <button className="usage-billing-tab active">{copy.tasks}</button>
        <button className="usage-billing-tab">{copy.websites}</button>
        <button className="usage-billing-tab">{copy.computers}</button>
      </div>

      {tierChangeResult && (
        <div className="usage-billing-notice">
          <Check size={16} />
          <div>
            <strong>{tierChangeResult.status === 'upgraded' ? 'Oppgradering vellykket!' : 'Nedgradering planlagt'}</strong>
            <p>{tierChangeResult.message}</p>
          </div>
          <button onClick={() => setTierChangeResult(null)}><X size={14} /></button>
        </div>
      )}

      <section className="usage-billing-card">
        <div className="usage-billing-card-head">
          <div>
            <h3>{planTitle}</h3>
            <p>{planMeta}</p>
          </div>
          <div className="usage-billing-actions">
            <div className="usage-billing-manage-wrap">
              <button className="usage-billing-secondary-btn" onClick={() => setManageOpen(v => !v)}>
                {copy.manage} <ChevronDown size={14} />
              </button>
              {manageOpen && (
                <div className="usage-billing-menu">
                  <button onClick={() => { setManageOpen(false); setShowTierModal(true) }}>{copy.manageSubscription}</button>
                  <button onClick={openInvoices}>{copy.viewInvoices}</button>
                </div>
              )}
            </div>
            <button className="usage-billing-secondary-btn" onClick={() => setShowTierModal(true)}>
              {copy.topUp}
            </button>
            <button className="usage-billing-primary-btn" onClick={() => setShowTierModal(true)}>
              {isPro ? copy.changeLevel : copy.upgrade}
            </button>
          </div>
        </div>

        <div className="usage-billing-metrics">
          <div className="usage-billing-metric">
            <div className="usage-billing-metric-label">
              <Sparkles size={15} />
              <strong>{copy.credits}</strong>
              <HelpCircle size={13} />
            </div>
            <div className="usage-billing-metric-value">{availableCredits.toLocaleString(locale)}</div>
            <div className="usage-billing-metric-row">
              <span>{copy.freeCredits}</span>
              <span>{Math.max(0, availableCredits - monthlyLimit).toLocaleString(locale)}</span>
            </div>
            <div className="usage-billing-metric-row">
              <span>{copy.monthlyCredits}</span>
              <span>{availableCredits.toLocaleString(locale)} / {monthlyLimit.toLocaleString(locale)}</span>
            </div>
          </div>

          <div className="usage-billing-metric">
            <div className="usage-billing-metric-label">
              <Calendar size={15} />
              <strong>{copy.dailyRefresh}</strong>
              <HelpCircle size={13} />
            </div>
            <div className="usage-billing-metric-value">0</div>
            <p>{copy.refreshInfo.replace('{{count}}', String(dailyRefresh))}</p>
          </div>
        </div>
      </section>

      <section className="usage-billing-record">
        <h3>{copy.usageRecord}</h3>
        <div className="usage-billing-table">
          <div className="usage-billing-table-head">
            <span>{copy.details}</span>
            <span>{copy.date}</span>
            <span>{copy.creditsChange}</span>
          </div>
          {usageRows.length === 0 && <div className="usage-billing-empty">{copy.noUsage}</div>}
          {usageRows.map(row => (
            <div key={row.id} className="usage-billing-table-row">
              <span title={row.details}>{row.details}</span>
              <span>{row.date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })}</span>
              <span className={row.amount > 0 ? 'is-positive' : 'is-negative'}>
                {row.amount > 0 ? '+' : ''}{row.amount.toLocaleString(locale)}
              </span>
            </div>
          ))}
        </div>
      </section>

      {showTierModal && (
        <TierSelectorModal
          onClose={() => setShowTierModal(false)}
          isPro={isPro}
          currentPlanId={currentPlanId}
          onSuccess={handleTierSuccess}
        />
      )}
    </div>
  )
}
